/**
 * View Component: MobileBottomNav
 * Mobile bottom navigation bar with filter/sort/home/fav buttons.
 * Opens bottom sheet filter drawer on filter tap.
 *
 * T-Cron-2026-10-22 20:00 (round-4-of-day) — 改进清单 #10「无障碍」
 *
 * 修的问题(实测 aria-label 覆盖率 = 0):
 *   1. `<nav>` 没有 aria-label —— 页面里有多个 landmark 时读屏用户
 *      只能听到"导航",不知道是哪一个(底部导航 / 侧栏 / 面包屑)。
 *   2. 当前 tab 只有 CSS class 表达状态(`.active` 变红),
 *      读屏**完全不知道用户现在在哪个 tab**。
 *      ⇒ 加 aria-current="page"。
 *   3. 三个按钮都是纯图标 + 文字,没有 aria-label ——
 *      「已选 3」这个按钮的可见文本是动态的,读屏念出来是
 *      "已选 3",用户不知道按下去会切到"收藏"视图。
 *   4. 「筛选」按钮按下后会弹出抽屉,但没有 aria-expanded ——
 *      读屏用户不知道抽屉现在是开是合。
 *
 * Props:
 *   onOpenFilter: () => void
 *   onBackToTop: () => void
 *   onToggleFav: () => void
 *   onlyFavorites: boolean
 *   activeTab: 'home' | 'favorites'
 *   onTabChange: (tab) => void
 *
 * ────────────────────────────────────────────────────────────────────────────
 * T-Cron-2026-10-31 20:00 (T126) — 改进清单 #3「触感反馈」
 * ────────────────────────────────────────────────────────────────────────────
 *
 * 底部导航是**这个 app 里最常被点**的控件(首页 / 筛选 / 已选 三选一,
 * 每次浏览都要点),但它是全站仅有的几个「高频点击却零触感反馈」的组件之一。
 * ⇒ 接 harness/haptics.js 的 buzz(),口径与 PostCard / Sidebar /
 *   FavoriteBar / InstallButton 完全一致:
 *     · 不支持 navigator.vibrate 的设备(桌面 Safari / Firefox)静默跳过;
 *     · 用户开了「减弱动态效果」⇒ 静默跳过;
 *     · 内部 try/catch ⇒ 触感永远不能把主流程带崩。
 *
 * 顺带修一个**更隐蔽**的问题:底栏三个按钮里,「筛选」会弹出抽屉,
 * 但「首页」和「已选」是**切换 tab** —— 它们的 aria-current 只表达
 * 「当前在哪个 tab」,但用户按下**另一个** tab 时,读屏用户没有任何
 * 反馈被告知「视图已切换」(tab 切换后焦点还留在按钮上,live region 不播报)。
 * ⇒ 加一个 aria-live="polite" 的状态播报区,把当前 tab 念出来。
 *   · 只用文本节点,不加 aria-label(给 live region 命名是反模式,
 *     见 T123 的记录:部分读屏会改为播报可访问名而丢掉真实内容);
 *   · 纯文本 + sr-only,零视觉影响。
 * ========================================================================== */
import { buzz } from '../harness/haptics.js';

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
    </svg>
  );
}

function SearchIconMobile() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  );
}

function MobileBottomNav({ onOpenFilter, activeTab, onTabChange, favCount, filterOpen }) {
  // 🔴 触感反馈(改进清单 #3)。口径与全站一致,详见文件头。
  const handleTabChange = React.useCallback((tab) => {
    buzz(8);
    onTabChange(tab);
  }, [onTabChange]);

  const handleOpenFilter = React.useCallback(() => {
    buzz(8);
    onOpenFilter();
  }, [onOpenFilter]);

  // 当前 tab 的文字播报。
  //   · 只用文本节点,**不加 aria-label**(给 live region 命名是反模式,
  //     部分读屏会改为播报可访问名而丢掉真实内容 —— 见 T123 记录)。
  //   · aria-live="polite" ⇒ 等用户空闲时再念,不打断当前朗读。
  //   · .sr-only 是 polish.css 里现成的视觉隐藏工具类 ⇒ 零视觉影响。
  const currentTabLabel = activeTab === 'favorites'
    ? `已切换到已选素材视图,共 ${favCount > 0 ? `${favCount} 篇` : '0 篇'}`
    : '已切换到首页,全部选题';

  return (
    <nav className="mobile-bottom-nav" aria-label="底部导航">
      <button
        className={`mobile-nav-item ${activeTab === 'home' ? 'active' : ''}`}
        onClick={() => handleTabChange('home')}
        aria-label="首页,全部选题"
        aria-current={activeTab === 'home' ? 'page' : undefined}
      >
        <HomeIcon />
        <span>首页</span>
      </button>
      <button
        className="mobile-nav-item"
        onClick={handleOpenFilter}
        aria-label="筛选,按书名/日期/热点/批次缩小范围"
        aria-expanded={!!filterOpen}
        aria-haspopup="dialog"
      >
        <FilterIcon />
        <span>筛选</span>
      </button>
      <button
        className={`mobile-nav-item ${activeTab === 'favorites' ? 'active' : ''}`}
        onClick={() => handleTabChange('favorites')}
        aria-label={favCount > 0 ? `已选素材,共 ${favCount} 篇` : '已选素材,当前为空'}
        aria-current={activeTab === 'favorites' ? 'page' : undefined}
      >
        <HeartIcon />
        <span>已选{favCount > 0 ? ` ${favCount}` : ''}</span>
      </button>
      {/* 视图切换播报(纯读屏,视觉上不可见)。 */}
      <span className="sr-only" role="status" aria-live="polite">{currentTabLabel}</span>
    </nav>
  );
}

window.MobileBottomNav = MobileBottomNav;

export default MobileBottomNav;
